import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { TextVariant } from '@naf/text';
import { CarMeta } from '@appTypes/carModelType';
import { Season } from '@appTypes/ElPrixContextData';
import { TestTabs } from './TestTabs';
import { formatTestSeason } from './RangeTests';
import { InfoBox } from './InfoBox';
import { SectionHeader } from './ModelDetailPage/Styles';
import { ChargeTestContent, TestData } from './ChargeTestsContent';
import { useTestTabSelection } from './useTestTabSelection';

type TestGroup = Record<`${Season}_${string}`, { tests: { [key: string]: TestData } }>;

type Props = Pick<CarMeta, 'chargeTests' | 'cloudinaryImages' | 'rangeTests'> & {
  chargeTestInfoBox: CarMeta['globalContent']['chargeTestInfoBox'];
  slug: string;
};

export const ChargeTests = ({ chargeTests, chargeTestInfoBox, cloudinaryImages, rangeTests, slug }: Props) => {
  const testGroups: TestGroup = useMemo(
    () =>
      chargeTests.reduce((acc, chargeTest) => {
        const { season, testDate } = chargeTest.context;
        const testKey = `${season}_${testDate}`;
        const testId = (chargeTest as any).id;

        const rangeTestResults = (rangeTests ?? []).find((rangeTest) => (rangeTest as any).id === testId);

        let statedRangeKm: number | undefined;
        let statedConsumptionKWhper100km: number | undefined;

        if (rangeTestResults) {
          statedRangeKm = rangeTestResults.statedRangeKm;
          statedConsumptionKWhper100km = rangeTestResults.statedConsumptionKWhper100km;
        }

        if (acc[testKey]) {
          acc[testKey] = {
            ...acc[testKey],
            tests: { ...acc[testKey].tests, [testId]: { ...chargeTest, statedRangeKm, statedConsumptionKWhper100km } },
          };
        } else {
          acc[testKey] = {
            tests: { [testId]: { ...chargeTest, statedRangeKm, statedConsumptionKWhper100km } },
          };
        }
        return acc;
      }, {}),
    [chargeTests, rangeTests],
  );

  // Entries rather than keys, so each context carries its own tests and the strip can be built
  // from one list instead of joining two by index.
  const tabContexts = useMemo(
    () =>
      Object.entries(testGroups)
        .sort(([a], [b]) => new Date(b.split('_')[1]).getTime() - new Date(a.split('_')[1]).getTime())
        .map(([key, { tests }]) => {
          const [season, date] = key.split('_') as [Season, string];
          return { key, value: formatTestSeason(season, date), tests };
        }),
    [testGroups],
  );

  const { selectedTab, selectTabByKey } = useTestTabSelection(tabContexts);

  const items = tabContexts.map(({ key, value, tests }) => ({
    value: key,
    label: value,
    content: <ChargeTestContent slug={slug} tests={tests} sanityImages={cloudinaryImages} />,
  }));

  return (
    <>
      <ScrollAnchorTag id="ladetester">
        <SectionHeader variant={TextVariant.Header1} tag="h2">
          Test av lading
        </SectionHeader>
      </ScrollAnchorTag>
      <TestTabs items={items} selectedTab={selectedTab} onSelectTab={selectTabByKey} />
      {chargeTestInfoBox && <InfoBox title={chargeTestInfoBox.title} text={chargeTestInfoBox.text} />}
    </>
  );
};

const ScrollAnchorTag = styled.div`
  scroll-margin: 100px;
`;
